import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "./useAccount";
import { Avviso, Bottone, BottoneEsci, BottoneMenu, Campo, LinkTesto, PaginaAccount, Riga, Scheda, dataIt, useMobile } from "./ui";
import Sidebar from "./Sidebar";
import { traccia } from "../consenso/tracciamento";
import { C, F } from "../theme/tokens";
import { LISTINO_STATICO, NOTA_LISTINO, pianiAbbonabili } from "../pricing/listino";
import { BottonePiano, PIANO_IN_EVIDENZA, SchedaPiano, SelettoreCadenza, TINTE, azionePiano } from "../pricing/SchedaPiano";
import { dimenticaPianoScelto, leggiPianoScelto } from "./RegistrazionePage";

const STATI = {
  attivo: { testo: "Attivo", colore: "#1f6b3a", sfondo: "#eefaf2" },
  in_sospeso: { testo: "Pagamento in ritardo", colore: "#8a5a00", sfondo: "#fff8e6" },
  disdetto: { testo: "Disdetto", colore: "#b42318", sfondo: "#fff0f0" },
  nessuno: { testo: "Nessun abbonamento", colore: C.textNum, sfondo: C.sectionAlt },
};

function Etichetta({ stato }) {
  const s = STATI[stato] || STATI.nessuno;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, color: s.colore, background: s.sfondo }}>{s.testo}</span>;
}

function BarraConsumo({ consumo }) {
  if (!consumo) return null;
  const incluse = consumo.pagine_incluse;
  if (incluse === null || incluse === undefined) {
    return (
      <p style={{ margin: "8px 0 0", fontSize: 13, color: C.textBody }}>
        Pagine illimitate. Elaborate in questo mese: <strong>{consumo.pagine_usate}</strong> su {consumo.lavori} {consumo.lavori === 1 ? "documento" : "documenti"}.
      </p>
    );
  }
  const quota = Math.min(1, incluse ? consumo.pagine_usate / incluse : 0);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textBody, marginBottom: 6 }}>
        <span><strong>{consumo.pagine_usate}</strong> di {incluse} pagine incluse usate questo mese</span>
        <span>{consumo.pagine_rimanenti} rimanenti</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: C.smartBlue20, overflow: "hidden" }}>
        <div style={{ width: `${Math.round(quota * 100)}%`, height: "100%", background: quota >= 1 ? "#d97706" : C.smartBlue, transition: "width .3s ease" }} />
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 12, color: C.textNum }}>
        Mese di quota dal {dataIt(consumo.finestra_inizio)} al {dataIt(consumo.finestra_fine)}.
        {consumo.pagine_extra > 0 && <> Pagine oltre le incluse: <strong>{consumo.pagine_extra}</strong>, a {String(consumo.prezzo_pagina_extra_eur).replace(".", ",")} € l&apos;una sulla prossima fattura.</>}
      </p>
    </div>
  );
}

/**
 * Le schede dei piani, come nella home (senza Enterprise).
 *
 * Senza abbonamento ogni scheda attiva il piano con il checkout. Con un
 * abbonamento la scheda del piano in corso e' segnata e spenta; le altre
 * portano al portale Stripe, dove il cambio si completa con il conguaglio
 * (il portale e' configurato per i sei prezzi, vedi stripe_setup.py).
 */
function Piani({ api, account, preselezionato, isMobile, onErrore, onPortale, portaleInCorso }) {
  const { azienda, utente } = account;
  const titolare = utente.ruolo === "titolare";
  const abbonata = Boolean(azienda.abbonata);
  const [listino, setListino] = useState(LISTINO_STATICO);
  const [cadenza, setCadenza] = useState(preselezionato?.cadenza || (abbonata && azienda.cadenza) || "mensile");
  const [inCorso, setInCorso] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.piani().then((r) => { if (vivo && Array.isArray(r?.piani) && r.piani.length) setListino(r.piani); }).catch(() => {});
    return () => { vivo = false; };
  }, [api]);

  const attiva = async (piano) => {
    setInCorso(piano);
    onErrore(null);
    try {
      const { url } = await api.checkout(piano, cadenza);
      dimenticaPianoScelto();
      const scelto = pianiAbbonabili(listino).find((p) => p.chiave === piano);
      traccia.abbonamentoCheckout({ piano, cadenza, importo: cadenza === "annuale" ? scelto?.prezzo?.annuale_eur : scelto?.prezzo?.mensile_eur });
      window.location.assign(url);
    } catch (err) {
      onErrore(err.message);
      setInCorso(null);
    }
  };

  const abbonabili = pianiAbbonabili(listino);
  return (
    <div>
      {!abbonata && azienda.stato === "disdetto" && <Avviso tipo="attenzione">L&apos;abbonamento precedente e&apos; stato disdetto. Puoi attivarne uno nuovo quando vuoi.</Avviso>}
      {!titolare && <Avviso tipo="info">Solo il titolare dell&apos;account pu&ograve; {abbonata ? "cambiare piano" : "attivare un abbonamento"}.</Avviso>}
      <div style={{ marginBottom: 24 }}>
        <SelettoreCadenza cadenza={cadenza} onChange={setCadenza} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 16, paddingTop: 6 }}>
        {abbonabili.map((p) => {
          const a = azionePiano(p, cadenza, abbonata ? azienda : null, abbonabili);
          const consigliato = !abbonata && (preselezionato ? preselezionato.piano === p.chiave : p.chiave === PIANO_IN_EVIDENZA);
          const sopra = a.tipo === "attuale" ? "IL TUO PIANO" : a.tipo === "su" ? "UPGRADE" : consigliato ? "PIÙ SCELTO" : null;
          const disabilitato = !titolare || !!inCorso || portaleInCorso;
          const variante = a.tipo === "attuale" ? "spento" : a.tipo === "giu" || a.tipo === "cadenza" ? "chiaro" : consigliato || a.tipo === "su" ? "blu" : "scuro";
          const etichetta = inCorso === p.chiave ? "Apertura del pagamento..."
            : portaleInCorso && a.tipo !== "attuale" && a.tipo !== "attiva" ? "Apertura del portale..."
              : a.etichetta;
          return (
            <SchedaPiano
              key={p.chiave}
              piano={p}
              cadenza={cadenza}
              tinta={TINTE[p.chiave]}
              evidenza={consigliato || a.tipo === "attuale"}
              etichettaAlto={sopra}
              isMobile={isMobile}
              bottone={<BottonePiano etichetta={etichetta} variante={variante} disabilitato={disabilitato} onClick={a.tipo === "attiva" ? () => attiva(p.chiave) : onPortale} />}
            />
          );
        })}
      </div>
      <p style={{ margin: "16px 0 0", fontSize: 12, color: C.textNum, lineHeight: 1.5 }}>{NOTA_LISTINO}</p>
    </div>
  );
}

function Team({ account, api, imposta }) {
  const [email, setEmail] = useState("");
  const [errore, setErrore] = useState(null);
  const [esito, setEsito] = useState(null);
  const [inCorso, setInCorso] = useState(false);
  const utenti = account.utenti || [];
  const inviti = account.inviti || [];
  const max = account.azienda.utenti_max;
  const occupati = utenti.length + inviti.length;

  const invita = async (e) => {
    e.preventDefault();
    setInCorso(true); setErrore(null); setEsito(null);
    try {
      imposta(await api.invita(email));
      setEsito(`Invito inviato a ${email.trim().toLowerCase()}.`);
      setEmail("");
    } catch (err) {
      setErrore(err.message);
    } finally {
      setInCorso(false);
    }
  };

  const revoca = async (indirizzo) => {
    setErrore(null);
    try { imposta(await api.revocaInvito(indirizzo)); } catch (err) { setErrore(err.message); }
  };

  const rimuovi = async (u) => {
    if (!window.confirm(`Rimuovere ${u.email} dal team? Non potra' piu' accedere.`)) return;
    setErrore(null);
    try { imposta(await api.rimuoviUtente(u.id)); } catch (err) { setErrore(err.message); }
  };

  return (
    <Scheda titolo="Team" descrizione={`${occupati} di ${max} ${max === 1 ? "posto" : "posti"} occupati, inviti compresi.`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {utenti.map((u) => (
          <div key={u.id} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.smartWhite }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 14, color: C.textTitle, fontWeight: 600 }}>{u.nome}{u.id === account.utente.id && <span style={{ fontWeight: 400, color: C.textNum, fontSize: 12 }}> · tu</span>}</div>
              <div style={{ fontSize: 12, color: C.textNum }}>{u.email}{u.ultimo_accesso ? ` · ultimo accesso ${dataIt(u.ultimo_accesso)}` : ""}</div>
            </div>
            {u.id !== account.utente.id && <Bottone variante="pericolo" piccolo onClick={() => rimuovi(u)}>Rimuovi</Bottone>}
          </div>
        ))}
        {inviti.map((i) => (
          <div key={i.email} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, border: `1px dashed ${C.smartBlue30}`, background: C.smartBlue10 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 14, color: C.textTitle }}>{i.email}</div>
              <div style={{ fontSize: 12, color: C.textNum }}>Invito in attesa, scade il {dataIt(i.scade_il)}</div>
            </div>
            <Bottone variante="secondario" piccolo onClick={() => revoca(i.email)}>Revoca</Bottone>
          </div>
        ))}
      </div>
      {account.azienda.abbonata ? (
        <form onSubmit={invita}>
          <Riga>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Campo etichetta="Invita un collega" tipo="email" valore={email} onChange={setEmail} placeholder="nome@azienda.it" />
            </div>
            <Bottone tipo="submit" disabilitato={inCorso || occupati >= max} style={{ marginTop: 8 }}>{inCorso ? "Invio..." : "Invita"}</Bottone>
          </Riga>
        </form>
      ) : (
        <p style={{ fontSize: 13, color: C.textNum, margin: 0 }}>Gli inviti si attivano con un abbonamento.</p>
      )}
      {esito && <Avviso tipo="ok" style={{ marginTop: 10 }}>{esito}</Avviso>}
      {errore && <Avviso tipo="errore" style={{ marginTop: 10 }}>{errore}</Avviso>}
    </Scheda>
  );
}

function CambioPassword({ api }) {
  const [vecchia, setVecchia] = useState("");
  const [nuova, setNuova] = useState("");
  const [errore, setErrore] = useState(null);
  const [esito, setEsito] = useState(null);

  const invia = async (e) => {
    e.preventDefault();
    setErrore(null); setEsito(null);
    try {
      const r = await api.cambiaPassword(vecchia, nuova);
      setEsito(r.messaggio || "Password aggiornata.");
      setVecchia(""); setNuova("");
    } catch (err) {
      setErrore(err.message);
    }
  };

  return (
    <Scheda titolo="Password">
      <form onSubmit={invia}>
        <Campo etichetta="Password attuale" tipo="password" valore={vecchia} onChange={setVecchia} autoComplete="current-password" />
        <Campo etichetta="Nuova password" tipo="password" valore={nuova} onChange={setNuova} autoComplete="new-password" minLength={10} aiuto="Almeno 10 caratteri. Le altre sessioni aperte verranno chiuse." />
        {esito && <Avviso tipo="ok">{esito}</Avviso>}
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
        <Bottone tipo="submit" variante="secondario">Aggiorna la password</Bottone>
      </form>
    </Scheda>
  );
}

/**
 * I diritti sui dati, in un posto solo: correggere nome, azienda e indirizzo
 * (art. 16), scaricare la copia dei propri dati (artt. 15 e 20), eliminare
 * l'account (art. 17). Il titolare che elimina l'account elimina l'azienda
 * intera, team compreso, e l'abbonamento si annulla subito.
 */
function TuoiDati({ account, api, imposta, onEliminato }) {
  const { utente, azienda } = account;
  const titolare = utente.ruolo === "titolare";
  const [nome, setNome] = useState(utente.nome);
  const [nomeAzienda, setNomeAzienda] = useState(azienda.nome);
  const [nuovaEmail, setNuovaEmail] = useState("");
  const [passwordEmail, setPasswordEmail] = useState("");
  const [passwordElimina, setPasswordElimina] = useState("");
  const [confermaElimina, setConfermaElimina] = useState(false);
  const [esito, setEsito] = useState({});
  const [inCorso, setInCorso] = useState(null);

  const esegui = async (chiave, azione) => {
    setInCorso(chiave);
    setEsito((e) => ({ ...e, [chiave]: null }));
    try {
      const messaggio = await azione();
      setEsito((e) => ({ ...e, [chiave]: { tipo: "ok", testo: messaggio } }));
    } catch (err) {
      setEsito((e) => ({ ...e, [chiave]: { tipo: "errore", testo: err.message } }));
    } finally {
      setInCorso(null);
    }
  };

  const salvaProfilo = (ev) => {
    ev.preventDefault();
    esegui("profilo", async () => {
      imposta(await api.aggiornaProfilo({ nome, azienda: titolare ? nomeAzienda : undefined }));
      return "Dati aggiornati.";
    });
  };
  const cambiaEmail = (ev) => {
    ev.preventDefault();
    esegui("email", async () => {
      const r = await api.cambiaEmail(nuovaEmail, passwordEmail);
      setNuovaEmail(""); setPasswordEmail("");
      return r.messaggio || "Controlla la posta del nuovo indirizzo.";
    });
  };
  const scarica = () => esegui("copia", async () => {
    const { nome: file, contenuto } = await api.esporta();
    const url = URL.createObjectURL(contenuto);
    const a = document.createElement("a");
    a.href = url; a.download = file;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return "Copia scaricata.";
  });
  const elimina = (ev) => {
    ev.preventDefault();
    esegui("elimina", async () => {
      await api.elimina(passwordElimina);
      onEliminato();
      return "Account eliminato.";
    });
  };
  const mostra = (chiave) => esito[chiave] && <Avviso tipo={esito[chiave].tipo} style={{ marginTop: 10 }}>{esito[chiave].testo}</Avviso>;
  const sottotitolo = { margin: "22px 0 8px", fontSize: 15, fontWeight: 600, fontFamily: F.h, color: C.textTitle };

  return (
    <Scheda titolo="I tuoi dati" descrizione="Correggi i tuoi dati, scaricane una copia o elimina l'account. I dati di fatturazione si gestiscono dal portale Stripe.">
      <form onSubmit={salvaProfilo}>
        <Campo etichetta="Il tuo nome" valore={nome} onChange={setNome} autoComplete="name" />
        {titolare && <Campo etichetta="Azienda o studio" valore={nomeAzienda} onChange={setNomeAzienda} autoComplete="organization" />}
        <Bottone tipo="submit" variante="secondario" disabilitato={inCorso === "profilo"}>Salva</Bottone>
        {mostra("profilo")}
      </form>

      <h3 style={sottotitolo}>Indirizzo email</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: C.textNum, lineHeight: 1.5 }}>
        Ora usi <strong>{utente.email}</strong>. Il nuovo indirizzo vale dopo che l&apos;avrai confermato dal link che gli mandiamo.
      </p>
      <form onSubmit={cambiaEmail}>
        <Campo etichetta="Nuovo indirizzo" tipo="email" valore={nuovaEmail} onChange={setNuovaEmail} autoComplete="email" />
        <Campo etichetta="Password attuale" tipo="password" valore={passwordEmail} onChange={setPasswordEmail} autoComplete="current-password" />
        <Bottone tipo="submit" variante="secondario" disabilitato={inCorso === "email"}>Cambia indirizzo</Bottone>
        {mostra("email")}
      </form>

      <h3 style={sottotitolo}>Copia dei tuoi dati</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: C.textNum, lineHeight: 1.5 }}>
        Un file JSON con account, {titolare ? "azienda, team, " : ""}accessi e lavori: tutto quello che conserviamo su di te. I documenti non ci sono perché non li conserviamo.
      </p>
      <Bottone variante="secondario" onClick={scarica} disabilitato={inCorso === "copia"}>{inCorso === "copia" ? "Preparazione..." : "Scarica i tuoi dati"}</Bottone>
      {mostra("copia")}

      <h3 style={{ ...sottotitolo, color: "#b42318" }}>Elimina l&apos;account</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: C.textNum, lineHeight: 1.5 }}>
        {titolare
          ? "Elimina l'azienda con tutto il team e i lavori. L'abbonamento viene annullato subito, senza rimborso del periodo in corso."
          : "Elimina il tuo utente. L'account dell'azienda e i colleghi restano."}
        {" "}Le fatture restano presso Stripe e Fatture in Cloud per il tempo previsto dalla legge. L&apos;operazione non si può annullare.
      </p>
      <form onSubmit={elimina}>
        <Campo etichetta="Password" tipo="password" valore={passwordElimina} onChange={setPasswordElimina} autoComplete="current-password" />
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: C.textBody, lineHeight: 1.5, margin: "0 0 14px", cursor: "pointer" }}>
          <input type="checkbox" checked={confermaElimina} onChange={() => setConfermaElimina((v) => !v)} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
          <span>{titolare ? "Ho capito: elimino l'azienda, il team e l'abbonamento." : "Ho capito: elimino il mio utente."}</span>
        </label>
        <Bottone tipo="submit" variante="pericolo" disabilitato={!confermaElimina || !passwordElimina || inCorso === "elimina"}>
          {inCorso === "elimina" ? "Eliminazione..." : "Elimina l'account"}
        </Bottone>
        {mostra("elimina")}
      </form>
    </Scheda>
  );
}

export default function AccountPage({ onNavigate, params = {} }) {
  const { account, caricamento, esci, imposta, api } = useAccount();
  const [errore, setErrore] = useState(null);
  const [avviso, setAvviso] = useState(null);
  const [inCorso, setInCorso] = useState(null);
  const [preselezionato] = useState(() => leggiPianoScelto());
  const sincronizzato = useRef(false);
  const isMobile = useMobile();
  const [sidebarAperta, setSidebarAperta] = useState(false);

  useEffect(() => {
    if (!caricamento && account === null) onNavigate("accesso", { next: "account" });
  }, [caricamento, account, onNavigate]);

  const sincronizza = useCallback(async (silenzioso = false) => {
    setInCorso("sincronizza");
    try {
      const profilo = await api.sincronizza();
      imposta(profilo);
      if (!silenzioso) setAvviso("Stato dell'abbonamento aggiornato da Stripe.");
      return profilo;
    } catch (err) {
      if (!silenzioso) setErrore(err.message);
      return null;
    } finally {
      setInCorso(null);
    }
  }, [api, imposta]);

  useEffect(() => {
    if (!account || sincronizzato.current) return;
    if (params.verifica === "ok") setAvviso("Indirizzo confermato: benvenuto in OBSCURA.");
    if (params.verifica === "email") setAvviso("Nuovo indirizzo confermato: da adesso accedi con questo.");
    if (params.abbonamento === "ok" && account.utente.ruolo === "titolare") {
      sincronizzato.current = true;
      setAvviso("Pagamento ricevuto: attivazione dell'abbonamento in corso...");
      sincronizza(true).then((profilo) => {
        setAvviso("Abbonamento attivo. Buon lavoro.");
        const az = profilo?.azienda;
        if (az?.abbonata) {
          const voce = LISTINO_STATICO.find((p) => p.chiave === az.piano);
          const importo = az.cadenza === "annuale" ? voce?.prezzo?.annuale_eur : voce?.prezzo?.mensile_eur;
          traccia.abbonamentoAttivato({ piano: az.piano, cadenza: az.cadenza, importo, chiave: az.periodo_inizio || az.piano });
        }
      });
    } else if (params.abbonamento === "annullato") {
      setAvviso("Pagamento annullato: nessun addebito. Puoi riprovare quando vuoi.");
    }
  }, [account, params.verifica, params.abbonamento, sincronizza]);

  const apriPortale = async () => {
    setInCorso("portale"); setErrore(null);
    try {
      const { url } = await api.portale();
      window.location.assign(url);
    } catch (err) {
      setErrore(err.message);
      setInCorso(null);
    }
  };

  if (!account) {
    return <PaginaAccount titolo="Area clienti"><p style={{ color: C.textNum }}>Caricamento...</p></PaginaAccount>;
  }

  const { utente, azienda, consumo } = account;
  const titolare = utente.ruolo === "titolare";

  return (
    <PaginaAccount
      titolo="Area clienti"
      larghezza={980}
      lato={(
        <Sidebar
          isMobile={isMobile}
          aperta={sidebarAperta}
          onChiudi={() => setSidebarAperta(false)}
          onNuova={() => { setSidebarAperta(false); onNavigate("anonymize"); }}
          onNavigateTo={(pagina) => { setSidebarAperta(false); onNavigate(pagina); }}
        />
      )}
      sinistra={isMobile ? <BottoneMenu onClick={() => setSidebarAperta(true)} /> : null}
      destra={<BottoneEsci onClick={async () => { await esci(); onNavigate("landing"); }} />}
    >
      {avviso && <Avviso tipo="ok">{avviso}</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Scheda>
        <Riga style={{ justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontFamily: F.h, color: C.textTitle }}>{azienda.nome}</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textNum }}>{utente.nome} · {utente.email}</p>
          </div>
          <Etichetta stato={azienda.stato} />
        </Riga>
      </Scheda>

      <Scheda titolo="Abbonamento">
        {azienda.abbonata && (
          <>
            <p style={{ margin: 0, fontSize: 15, color: C.textBody }}>
              Piano <strong>{azienda.piano_nome}</strong> {azienda.cadenza}
              {azienda.periodo_fine && <>, {azienda.disdetta_a_fine_periodo ? "termina" : "si rinnova"} il <strong>{dataIt(azienda.periodo_fine)}</strong></>}.
            </p>
            {azienda.stato === "in_sospeso" && (
              <Avviso tipo="attenzione" style={{ marginTop: 12 }}>
                L&apos;ultimo pagamento non e&apos; riuscito. Il servizio resta attivo mentre Stripe ritenta: aggiorna la carta dal portale per evitare la disdetta.
              </Avviso>
            )}
            <BarraConsumo consumo={consumo} />
            {titolare && (
              <Riga style={{ marginTop: 16 }}>
                <Bottone onClick={apriPortale} disabilitato={inCorso === "portale"}>{inCorso === "portale" ? "Apertura..." : "Gestisci abbonamento"}</Bottone>
                <Bottone variante="secondario" onClick={() => sincronizza(false)} disabilitato={inCorso === "sincronizza"}>Aggiorna</Bottone>
                <span style={{ fontSize: 12, color: C.textNum }}>Carta, disdetta e ricevute sono nel portale Stripe.</span>
              </Riga>
            )}
            <h3 style={{ margin: "30px 0 4px", fontSize: 16, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Cambia piano</h3>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: C.textNum, lineHeight: 1.5 }}>
              Il cambio di piano o di cadenza si completa nel portale Stripe: la differenza viene calcolata sui giorni che restano del periodo in corso.
            </p>
          </>
        )}
        <Piani api={api} account={account} preselezionato={preselezionato} isMobile={isMobile} onErrore={setErrore} onPortale={apriPortale} portaleInCorso={inCorso === "portale"} />
        {!azienda.abbonata && titolare && azienda.ha_cliente_stripe && (
          <p style={{ margin: "14px 0 0", fontSize: 12, color: C.textNum }}>
            Hai gia&apos; pagato e non vedi l&apos;abbonamento? <LinkTesto onClick={() => sincronizza(false)}>Aggiorna da Stripe</LinkTesto>
          </p>
        )}
      </Scheda>

      {titolare && <Team account={account} api={api} imposta={imposta} />}
      <CambioPassword api={api} />
      <TuoiDati account={account} api={api} imposta={imposta} onEliminato={() => { onNavigate("landing"); imposta(null); }} />
    </PaginaAccount>
  );
}
